import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db } from '../data/store';
import { SERVER_URL, MAPI_URL, syncedGet, syncedSet, beginUiEdit, endUiEdit } from '../data/serverStore';
import { printSampleLabels } from '../data/sampleLabelPrint';
import { useApprovalCheck } from './ApprovalGate';
import { PARAM_DEFS, AUTO_PACKAGES } from '../data/params';
import { getMergedParams, getAllAreas } from './ParamManager';
import { LOGO_BASE64 } from '../data/logo';
import { getCompanyContacts } from '../data/clientProfiles';

// TAB_LABELS is now dynamic — loaded from ParamManager (includes custom areas)
// Built at render time inside the component

const today   = () => new Date().toISOString().split('T')[0];
const weekOut = () => new Date(Date.now()+7*86400000).toISOString().split('T')[0];
const CUSTOM_PACKAGES_KEY = 'sl_custom_packages';

// Older installations stored custom packages in more than one shape. Normalize
// them for the UI without discarding any previously saved package.
function normalizeCustomPackages(value) {
  const makePackage = (entry, fallbackId) => {
    if (Array.isArray(entry)) {
      return {
        id: String(fallbackId),
        label: String(fallbackId),
        params: entry.filter(Boolean),
      };
    }
    if (!entry || typeof entry !== 'object') return null;
    const id = String(entry.id || entry.key || fallbackId);
    const label = String(entry.label || entry.name || fallbackId);
    const params = Array.isArray(entry.params)
      ? entry.params
      : Array.isArray(entry.parameters) ? entry.parameters : [];
    return { ...entry, id, label, params: params.filter(Boolean) };
  };

  if (Array.isArray(value)) {
    return value.map((entry, index) => {
      if (typeof entry === 'string') {
        return { id:`custom-${index}`, label:entry, params:[] };
      }
      return makePackage(entry, `custom-${index}`);
    }).filter(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, entry]) => makePackage(entry, key))
      .filter(Boolean);
  }
  return [];
}

// ── Print helpers ─────────────────────────────────────────────────────────────
function buildWorksheetHTML(samples, mode) {
  const wo      = samples[0]?.workOrder || '';
  const client  = samples[0]?.source || samples[0]?.client || '';
  const printed = new Date().toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
  const rushLabels = {1:'Same Day',2:'Next Day',3:'2-3 Days',4:'4-5 Days',5:'Standard'};
  const isWorkOrderPrint = mode === 'workorder';
  const isLaboratoryPrint = !isWorkOrderPrint;
  const formatPrintDate = value => {
    if (!value) return '';
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[2]}/${match[3]}/${match[1].slice(2)}` : value;
  };

  // ── Page header (reused on every page) ───────────────────────────────────
  const pageHeader = (title) => `
    <div class="page-header">
      <img src="${LOGO_BASE64}" alt="Sterling Analytical" style="height:44px;width:auto;"/>
      <div class="page-header-center">
        <div class="page-header-title">${title}</div>
        <div class="page-header-sub">Sterling Analytical, Inc. · 15 Agawam Avenue, West Springfield, MA 01089 · (413) 214-6541</div>
      </div>
      <div class="page-header-right">
        ${wo ? `<div><b>Work Order:</b> ${wo}</div>` : ''}
        <div><b>Client:</b> ${client}</div>
        <div><b>Printed:</b> ${printed}</div>
        <div><b>Samples:</b> ${samples.length}</div>
      </div>
    </div>`;

  // ── Work-order header — mirrors the compact legacy report ───────────────
  const firstSample = samples[0] || {};
  const workOrderHeader = `
    <div class="workorder-heading">
      <div class="sample-count"><span>Number of<br/>Samples</span><b>${samples.length}</b></div>
      <div class="workorder-title">Work Order Report</div>
      <img src="${LOGO_BASE64}" alt="Sterling Analytical" class="workorder-logo"/>
    </div>
    <div class="workorder-meta">
      <div class="meta-field meta-source"><span>Source</span><b>${client}</b></div>
      <div class="meta-field"><span>Received</span><b>${formatPrintDate(firstSample.received)}</b></div>
      <div class="meta-field"><span>Test Type</span><b>${firstSample.testType || ''}</b></div>
      <div class="meta-field"><span>Work Order</span><b>${wo}</b></div>
    </div>`;

  // ── Cover page — compact four-column grid ─────────────────────────────────
  const coverCards = samples.map((s,i) => {
    const params = s.params || [];
    const rush = s.rush ? parseInt(s.rush) : 5;
    const rushCol = rush<=1?'#b83232':rush<=2?'#e05500':rush<=3?'#e09900':'#1e3060';
    return `
      <div class="sample-block">
        <div class="taken-line">
          <span>Taken</span><b>${formatPrintDate(s.taken)}</b>
          ${rush<=3?`<span class="rush-badge" style="background:${rushCol}">R${rush}</span>`:''}
        </div>
        <div class="sample-card">
          <div class="sample-card-top">
            <b class="cover-sample-id">${s.id}</b>
            <b class="sample-tech">${s.tech||''}</b>
          </div>
          <div class="sample-description">${s.desc||''}</div>
          <div class="sample-card-bottom"><span>${s.sampleForm||''}</span><span><b>Due:</b> ${formatPrintDate(s.dueDate)}</span></div>
        </div>
        <div class="parameter-card">${params.length>0
          ? params.map(p=>`<div class="param-row">${p}</div>`).join('')
          : '<span class="no-params">None assigned</span>'
        }</div>
      </div>`;
  });
  const coverRows = Array.from({length:Math.ceil(coverCards.length / 4)}, (_,rowIndex) => `
    <div class="cover-row">${coverCards.slice(rowIndex * 4, rowIndex * 4 + 4).join('')}</div>
  `).join('');

  // ── Sample detail worksheets (one per sample, compact) ───────────────────
  const detailPages = samples.map((s,i) => {
    const params = s.params || [];
    // Split params into 2 columns for the results table
    const half  = Math.ceil(params.length / 2);
    const col1  = params.slice(0, half);
    const col2  = params.slice(half);
    const maxRows = Math.max(col1.length, col2.length);
    const resultRows = Array.from({length:maxRows}, (_,r) => `
      <tr>
        <td class="p1">${col1[r]||''}</td>
        <td class="rv"></td><td class="ru"></td><td class="rq"></td>
        <td class="sep"></td>
        <td class="p2">${col2[r]||''}</td>
        <td class="rv"></td><td class="ru"></td><td class="rq"></td>
      </tr>`).join('');

    return `
      <div class="detail-page">
        ${pageHeader(`Laboratory Worksheet — Sample #${s.id}`)}
        <table class="detail-info">
          <tr>
            <td class="dl">Sample #</td><td class="dv"><b>${s.id}</b></td>
            <td class="dl">Work Order</td><td class="dv"><b>${s.workOrder||''}</b></td>
            <td class="dl">Source / Client</td><td class="dv">${s.source||s.client||''}</td>
            <td class="dl">Rush</td>
            <td class="dv" style="color:${(s.rush&&parseInt(s.rush)<=3)?'#b83232':'#333'};font-weight:700">
              ${s.rush?`Level ${s.rush} — ${rushLabels[parseInt(s.rush)]||''}`:'Standard'}
            </td>
          </tr>
          <tr>
            <td class="dl">Description</td><td class="dv" colspan="3">${s.desc||''}</td>
            <td class="dl">Test Type</td><td class="dv">${s.testType||''}</td>
            <td class="dl">Sample Form</td><td class="dv">${s.sampleForm||''}</td>
          </tr>
          <tr>
            <td class="dl">Received</td><td class="dv">${s.received||''}</td>
            <td class="dl">Due Date</td><td class="dv">${s.dueDate||''}</td>
            <td class="dl">Assigned To</td><td class="dv">${s.tech||''}</td>
            <td class="dl">Amount</td><td class="dv">${s.amount||''}</td>
          </tr>
        </table>

        <table class="results-table">
          <thead>
            <tr>
              <th class="p1">Parameter</th>
              <th class="rv">Result</th>
              <th class="ru">Unit</th>
              <th class="rq">Qual</th>
              <th class="sep"></th>
              <th class="p2">Parameter</th>
              <th class="rv">Result</th>
              <th class="ru">Unit</th>
              <th class="rq">Qual</th>
            </tr>
          </thead>
          <tbody>
            ${params.length > 0 ? resultRows
              : `<tr><td colspan="9" style="color:#aaa;font-style:italic;padding:12px;text-align:center">No parameters assigned</td></tr>`
            }
          </tbody>
        </table>
        <div class="detail-footer">
          Technician signature: ___________________________ &nbsp;&nbsp;&nbsp;
          Date analyzed: ___________________________ &nbsp;&nbsp;&nbsp;
          Page ${isLaboratoryPrint ? i+1 : i+2} of ${isLaboratoryPrint ? samples.length : samples.length+1}
        </div>
      </div>`;
  }).join('');

  const css = `
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:${isWorkOrderPrint?'13pt':'16pt'};color:#111;background:white;}

    /* ── Page header ── */
    .page-header{display:flex;align-items:flex-start;gap:12px;padding:8px 16px 8px 16px;
      border-bottom:2px solid #1e3060;margin-bottom:10px;}
    .page-header-center{flex:1;text-align:center;}
    .page-header-title{font-size:20pt;font-weight:700;color:#1e3060;margin-bottom:2px;}
    .page-header-sub{font-size:12pt;color:#666;}
    .page-header-right{text-align:right;font-size:14pt;line-height:1.6;white-space:nowrap;}

    /* ── Legacy-style compact work-order page ── */
    .workorder-heading{display:grid;grid-template-columns:180px 1fr 190px;align-items:end;
      gap:10px;padding:5px 12px 0;}
    .sample-count{border:1px solid #444;display:flex;align-items:center;justify-content:space-between;
      padding:4px 8px;line-height:1.2;min-height:46px;}
    .sample-count b{font-size:13pt;}
    .workorder-title{text-align:center;font-size:13pt;font-weight:700;padding-bottom:7px;}
    .workorder-logo{height:44px;width:auto;max-width:190px;justify-self:end;}
    .workorder-meta{display:grid;grid-template-columns:minmax(0,3fr) minmax(100px,1fr) minmax(140px,1.35fr) minmax(120px,1fr);
      gap:5px;margin:2px 12px 8px;padding-bottom:7px;border-bottom:2px solid #333;}
    .meta-field span{display:block;font-size:10pt;margin:0 0 1px 3px;}
    .meta-field b{display:block;border:1px solid #555;padding:3px 5px;min-height:25px;font-size:13pt;
      line-height:1.15;overflow-wrap:normal;word-break:normal;white-space:normal;}
    .cover-grid{padding:0 12px;}
    .cover-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:6px;
      align-items:start;margin-bottom:6px;break-inside:avoid;page-break-inside:avoid;}
    .sample-block{break-inside:avoid;min-width:0;}
    .taken-line{display:flex;align-items:center;gap:6px;padding:1px 2px;font-size:11pt;min-height:23px;}
    .taken-line b{font-size:13pt;flex:1;}
    .sample-card{border:1px solid #555;min-height:91px;padding:5px;display:flex;flex-direction:column;}
    .sample-card-top{display:flex;align-items:center;justify-content:space-between;gap:5px;}
    .cover-sample-id{font-weight:700;font-size:13pt;color:#111;font-family:Arial,sans-serif;}
    .sample-tech{font-size:10pt;text-transform:none;}
    .sample-description{font-size:12pt;line-height:1.2;margin-top:4px;overflow-wrap:anywhere;}
    .sample-card-bottom{display:flex;justify-content:space-between;gap:5px;margin-top:auto;padding-top:5px;
      font-size:10pt;overflow-wrap:anywhere;}
    .parameter-card{border:1px solid #555;border-top:0;min-height:70px;padding:3px 5px;}
    .rush-badge{background:#b83232;color:white;font-size:9pt;font-weight:700;
      padding:1px 4px;border-radius:3px;}
    .param-row{display:block;color:#111;font-size:10pt;padding:1px 0;line-height:1.12;overflow-wrap:anywhere;}
    .no-params{color:#777;font-style:italic;font-size:10pt;}

    /* ── Detail page ── */

    .detail-info{width:100%;border-collapse:collapse;margin-bottom:10px;
      background:#f8f9ff;border:1px solid #d0d8ec;}
    .detail-info .dl{padding:3px 6px;font-weight:700;color:#1e3060;font-size:14pt;
      background:#edf0f8;border:1px solid #d0d8ec;width:80px;white-space:nowrap;}
    .detail-info .dv{padding:3px 7px;border:1px solid #d0d8ec;font-size:14pt;min-width:80px;}

    /* ── Results table (2 column) ── */
    .results-table{width:100%;border-collapse:collapse;}
    .results-table thead tr{background:#1e3060;color:white;}
    .results-table th{padding:6px 8px;text-align:left;font-size:14pt;font-weight:700;}
    .results-table td{padding:6px 8px;border-bottom:1px solid #e8ecf8;font-size:14pt;}
    .results-table tr:nth-child(even) td{background:#f8f9ff;}
    .p1{width:28%;} .p2{width:28%;}
    .rv{width:12%;border-left:1px solid #e8ecf8;}
    .ru{width:10%;} .rq{width:8%;}
    .sep{width:6px;background:#e0e4ec;border:none!important;padding:0!important;}

    .detail-footer{margin-top:10px;padding-top:6px;border-top:1px solid #ddd;
      font-size:14pt;color:#666;text-align:center;}

    /* ── Cover summary table ── */
    .cover-summary{width:100%;border-collapse:collapse;margin:0 10px 8px 10px;
      font-size:11pt;width:calc(100% - 20px);}
    .cover-summary th{background:#1e3060;color:white;padding:4px 6px;text-align:left;white-space:nowrap;}
    .cover-summary td{padding:3px 6px;border-bottom:1px solid #e8ecf8;}
    .cover-summary tr:nth-child(even) td{background:#f8f9ff;}

    .section-title{font-size:16pt;font-weight:700;color:#1e3060;padding:8px 16px 4px 16px;
      border-bottom:1px solid #e0e4ec;margin-bottom:8px;}
    .detail-page{page-break-before:always;padding:0 16px;}
    .detail-page:first-child{page-break-before:auto;}

    body.workorder-print .page-header-title,
    body.workorder-print .page-header-right,
    body.workorder-print .detail-footer,
    body.workorder-print .cover-summary{font-size:13pt;}

    @media print{
      .cover-card{break-inside:avoid;}
      .cover-grid{break-inside:auto;page-break-inside:auto;}
      .cover-row{break-inside:avoid;page-break-inside:avoid;}
      .detail-page{page-break-before:always;}
      .detail-page:first-child{page-break-before:auto;}
    }`;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>${isWorkOrderPrint ? `Work Order ${wo}` : `Laboratory Worksheet${samples.length!==1?'s':''} — Work Order ${wo}`} — Sterling Analytical</title>
    <style>${css}</style>
  </head><body class="${isWorkOrderPrint?'workorder-print':'worksheet-print'}">

  ${isWorkOrderPrint ? `
  <!-- ══ WORK ORDER ══ -->
  ${workOrderHeader}
  <div class="cover-grid">${coverRows}</div>
  ` : `
  <!-- ══ LABORATORY WORKSHEETS ONLY ══ -->
  ${detailPages}
  `}

  </body></html>`;
}

function openPrint(html) {
  const win = window.open('','_blank');
  win.document.write(html);
  win.document.close();
  win.onload = () => win.print();
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ allSamples, activeSampleId, onSelect, onSelectSample }) {
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('workorder'); // 'workorder' | 'id'
  const [printModal, setPrintModal] = useState(false);
  const [printSel, setPrintSel] = useState(new Set());

  // Group samples
  const filtered = useMemo(() => {
    if (!search) return allSamples;
    const q = search.toLowerCase();
    return allSamples.filter(s =>
      String(s.id).includes(q) ||
      s.workOrder?.toLowerCase().includes(q) ||
      (s.source||s.client||'').toLowerCase().includes(q)
    );
  }, [allSamples, search]);

  const groups = useMemo(() => {
    if (groupBy === 'id') {
      return [{ key:'All Samples', samples: filtered }];
    }
    const map = new Map();
    filtered.forEach(s => {
      const wo = s.workOrder || 'No WO';
      if (!map.has(wo)) map.set(wo, []);
      map.get(wo).push(s);
    });
    return [...map.entries()]
      .map(([key, samples]) => ({ key, samples }))
      .sort((a,b) => b.samples[0]?.id - a.samples[0]?.id);
  }, [filtered, groupBy]);

  const togglePrintSel = (id) => setPrintSel(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const printSelected = () => {
    const toPrint = allSamples.filter(s => printSel.has(s.id));
    if (toPrint.length === 0) return;
    openPrint(buildWorksheetHTML(toPrint, 'multi'));
    setPrintModal(false);
    setPrintSel(new Set());
  };

  const printWO = (woSamples) => {
    openPrint(buildWorksheetHTML(woSamples, 'workorder'));
  };

  return (
    <div style={{width:240,flexShrink:0,background:'white',borderRight:'1px solid #e2e6f0',
      display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0}}>

      {/* Sidebar header */}
      <div style={{background:'#1e3060',color:'white',padding:'8px 10px',flexShrink:0}}>
        <div style={{fontSize:12,fontWeight:700,marginBottom:6}}>📋 Samples</div>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search ID, WO, client..."
          style={{width:'100%',border:'none',borderRadius:4,padding:'5px 8px',
            fontSize:11,outline:'none',boxSizing:'border-box'}}/>
        <div style={{display:'flex',gap:4,marginTop:6}}>
          {[['workorder','By WO'],['id','By ID']].map(([v,l])=>(
            <button key={v} onClick={()=>setGroupBy(v)}
              style={{flex:1,padding:'3px 0',borderRadius:3,border:'none',
                fontSize:10,fontWeight:600,cursor:'pointer',
                background:groupBy===v?'rgba(255,255,255,0.25)':'rgba(0,0,0,0.2)',
                color:'white'}}>
              {l}
            </button>
          ))}
          <button onClick={()=>{ setPrintModal(true); setPrintSel(new Set()); }}
            style={{padding:'3px 8px',borderRadius:3,border:'none',fontSize:10,
              fontWeight:600,cursor:'pointer',background:'rgba(255,255,255,0.15)',
              color:'white'}} title="Print worksheets">
            🖨
          </button>
        </div>
      </div>

      {/* Sample list */}
      <div style={{flex:1,overflowY:'auto'}}>
        {groups.map(({ key, samples }) => (
          <div key={key}>
            {/* Work Order header */}
            {groupBy === 'workorder' && (
              <div style={{background:'#f0f4ff',padding:'4px 8px',fontSize:10,
                fontWeight:700,color:'#1e3060',display:'flex',
                alignItems:'center',justifyContent:'space-between',
                borderBottom:'1px solid #e0e4ec',position:'sticky',top:0,zIndex:1}}>
                <span>WO: {key} ({samples.length})</span>
                <button onClick={()=>printWO(samples)}
                  title={`Print all samples in WO ${key}`}
                  style={{background:'none',border:'none',cursor:'pointer',
                    fontSize:12,color:'#1e3060',padding:0}}>🖨</button>
              </div>
            )}
            {/* Sample rows */}
            {samples.map(s => {
              const isActive = String(s.id) === String(activeSampleId);
              const hasParams = (s.params||[]).length > 0;
              return (
                <div key={s.id}
                  onClick={()=>{ onSelect(s); onSelectSample(s); }}
                  style={{padding:'7px 10px',cursor:'pointer',
                    borderBottom:'1px solid #f0f2f8',
                    background: isActive ? '#ddeeff' : 'white',
                    borderLeft: isActive ? '3px solid #1e3060' : '3px solid transparent',
                    transition:'background 0.1s'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontFamily:'monospace',fontWeight:700,
                      fontSize:12,color:isActive?'#1e3060':'#333'}}>
                      {s.id}
                    </span>
                    {s.rush && parseInt(s.rush)<=3 && (
                      <span style={{background:'#b83232',color:'white',
                        fontSize:8,fontWeight:700,padding:'1px 4px',borderRadius:6}}>
                        R{s.rush}
                      </span>
                    )}
                    <span style={{marginLeft:'auto',fontSize:9,
                      color:hasParams?'#1a6b42':'#e09900',fontWeight:600}}>
                      {hasParams?`${s.params.length}p`:'—'}
                    </span>
                  </div>
                  <div style={{fontSize:10,color:'#888',marginTop:1,
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {s.source||s.client}
                  </div>
                  {s.tech && (
                    <div style={{fontSize:9,color:'#aaa',marginTop:1}}>
                      👤 {s.tech}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        {groups.length===0 && (
          <div style={{padding:16,textAlign:'center',color:'#999',fontSize:11}}>
            No samples found
          </div>
        )}
      </div>

      {/* Print selection modal */}
      {printModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',
          zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setPrintModal(false)}>
          <div style={{background:'white',borderRadius:8,width:480,maxHeight:'80vh',
            display:'flex',flexDirection:'column',boxShadow:'0 12px 40px rgba(0,0,0,0.3)'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{background:'#1e3060',color:'white',padding:'10px 16px',
              borderRadius:'8px 8px 0 0',display:'flex',
              alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontWeight:700,fontSize:13}}>🖨 Print Worksheets</div>
                <div style={{fontSize:10,color:'#aac4e0',marginTop:1}}>
                  Select samples or work orders to print
                </div>
              </div>
              <button onClick={()=>setPrintModal(false)}
                style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',
                  cursor:'pointer',fontSize:20}}>✕</button>
            </div>
            <div style={{padding:'10px 14px',borderBottom:'1px solid #e0e4ec',
              display:'flex',gap:8}}>
              <button className="btn btn-sm"
                onClick={()=>setPrintSel(new Set(allSamples.map(s=>s.id)))}>
                ☑ Select All
              </button>
              <button className="btn btn-sm"
                onClick={()=>setPrintSel(new Set())}>
                ☐ Clear
              </button>
              <span style={{fontSize:11,color:'#888',alignSelf:'center'}}>
                {printSel.size} selected
              </span>
            </div>
            <div style={{overflowY:'auto',flex:1}}>
              {/* Group by WO in print modal */}
              {groups.map(({key,samples})=>(
                <div key={key}>
                  {groupBy==='workorder' && (
                    <div style={{background:'#f0f4ff',padding:'5px 14px',
                      fontSize:10,fontWeight:700,color:'#1e3060',
                      display:'flex',alignItems:'center',gap:8,
                      borderBottom:'1px solid #e0e4ec'}}>
                      <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer'}}>
                        <input type="checkbox"
                          checked={samples.every(s=>printSel.has(s.id))}
                          onChange={e=>{
                            setPrintSel(prev=>{
                              const n=new Set(prev);
                              samples.forEach(s=>e.target.checked?n.add(s.id):n.delete(s.id));
                              return n;
                            });
                          }}
                          style={{accentColor:'#1e3060'}}/>
                        WO: {key} — {samples.length} sample{samples.length!==1?'s':''}
                      </label>
                      <button onClick={()=>printWO(samples)}
                        style={{marginLeft:'auto',background:'#1e3060',color:'white',
                          border:'none',borderRadius:3,padding:'2px 8px',
                          fontSize:10,cursor:'pointer',fontWeight:600}}>
                        🖨 Print WO
                      </button>
                    </div>
                  )}
                  {samples.map(s=>(
                    <label key={s.id}
                      style={{display:'flex',alignItems:'center',gap:10,
                        padding:'6px 14px',borderBottom:'1px solid #f0f2f8',
                        cursor:'pointer',background:printSel.has(s.id)?'#f0f6ff':'white'}}>
                      <input type="checkbox" checked={printSel.has(s.id)}
                        onChange={()=>togglePrintSel(s.id)}
                        style={{accentColor:'#1e3060',width:14,height:14}}/>
                      <span style={{fontFamily:'monospace',fontWeight:700,
                        fontSize:12,color:'#1e3060'}}>{s.id}</span>
                      <span style={{fontSize:11,color:'#555',flex:1,
                        overflow:'hidden',textOverflow:'ellipsis'}}>
                        {s.source||s.client}
                      </span>
                      <span style={{fontSize:10,color:'#888'}}>
                        {(s.params||[]).length}p
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <div style={{padding:'10px 14px',borderTop:'1px solid #e0e4ec',
              display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-primary"
                disabled={printSel.size===0}
                onClick={printSelected}
                style={{fontWeight:700}}>
                🖨 Print {printSel.size} Worksheet{printSel.size!==1?'s':''}
              </button>
              <button className="btn" onClick={()=>setPrintModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Results Sheet attachments (per sample) — same as EnterResults.jsx ──────
// Lets an on-site tech scan/upload a photo of their handwritten raw-results
// worksheet, so someone working remotely can view/download it later from
// Log Report without needing the physical paper handed to them.
const loadResultsSheetMap = () => {
  try { const v = syncedGet('results_sheets_by_sample', null); return v !== null ? JSON.parse(JSON.stringify(v)) : {}; }
  catch { return {}; }
};
const saveResultsSheetMap = (map) => { try { syncedSet('results_sheets_by_sample', map); } catch {} };
const getResultsSheetIds = (sampleId) => loadResultsSheetMap()[sampleId] || [];
const addResultsSheetId = (sampleId, sheetId) => {
  const map = loadResultsSheetMap();
  const existing = map[sampleId] || [];
  if (!existing.includes(sheetId)) saveResultsSheetMap({ ...map, [sampleId]: [...existing, sheetId] });
};
const removeResultsSheetId = (sampleId, sheetId) => {
  const map = loadResultsSheetMap();
  saveResultsSheetMap({ ...map, [sampleId]: (map[sampleId]||[]).filter(id => id !== sheetId) });
};

function ResultsSheetPanel({ sampleId, toast }) {
  const [sheetIds, setSheetIds] = useState(() => getResultsSheetIds(sampleId));
  const [scanning, setScanning] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [rotation, setRotation] = useState(90);
  const [zoom, setZoom] = useState(0.5);
  const fileInputRef = React.useRef();
  const resetView = () => { setRotation(90); setZoom(0.5); };

  useEffect(() => { setSheetIds(getResultsSheetIds(sampleId)); }, [sampleId]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const agentRes = await fetch(`${MAPI_URL}/health`);
      const agent = await agentRes.json();
      if (!agent.workstation) throw new Error('Windows agent workstation was not detected.');
      const res = await fetch(`${SERVER_URL}/scan-results-sheet`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          deviceIndex: 'prompt',
          workstation: agent.workstation,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Scan failed');
      addResultsSheetId(sampleId, data.sheetId);
      setSheetIds(getResultsSheetIds(sampleId));
      toast('success', 'Results sheet scanned and attached');
    } catch(e) {
      toast('error', e.message);
    } finally {
      setScanning(false);
    }
  };

  const handleUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/upload-results-sheet`, {
          method: 'POST', headers: {'Content-Type':'application/json'},
          body: JSON.stringify({ image: reader.result }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Upload failed');
        addResultsSheetId(sampleId, data.sheetId);
        setSheetIds(getResultsSheetIds(sampleId));
        toast('success', 'Results sheet uploaded and attached');
      } catch(e) {
        toast('error', e.message);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = (sheetId, e) => {
    e.stopPropagation();
    removeResultsSheetId(sampleId, sheetId);
    setSheetIds(getResultsSheetIds(sampleId));
  };

  const handlePrint = (url, rotationDeg) => {
    const isRotated = rotationDeg === 90 || rotationDeg === 270;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Print Results Sheet</title>
      <style>
        @page { size: ${isRotated ? 'landscape' : 'portrait'}; margin: 0.25in; }
        body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:white;}
        img{
          ${isRotated ? 'max-width:95vh;max-height:95vw;' : 'max-width:95vw;max-height:95vh;'}
          transform:rotate(${rotationDeg}deg);
        }
        @media print{ body{min-height:auto;} }
      </style></head><body>
      <img src="${url}" onload="window.print();"/>
      </body></html>`);
    win.document.close();
  };

  if (!sampleId) return null;

  return (
    <div style={{border:'1px solid #e0d8f0',borderRadius:6,padding:8,marginBottom:10,background:'#faf7ff'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700,color:'#7c3aed'}}>📄 Results Sheet(s)</span>
        <button onClick={handleScan} disabled={scanning}
          style={{fontSize:10,padding:'3px 8px',borderRadius:3,cursor:scanning?'wait':'pointer',
            border:'1px solid #7c3aed',background:'white',color:'#7c3aed'}}>
          {scanning ? '⏳ Scanning...' : '🖨 Scan'}
        </button>
        <button onClick={()=>fileInputRef.current?.click()}
          style={{fontSize:10,padding:'3px 8px',borderRadius:3,cursor:'pointer',
            border:'1px solid #7c3aed',background:'white',color:'#7c3aed'}}>
          📁 Upload
        </button>
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" style={{display:'none'}}
          onChange={e=>{ handleUpload(e.target.files[0]); e.target.value=''; }}/>
        {sheetIds.map(id=>(
          <div key={id} style={{position:'relative'}}>
            <img src={`${SERVER_URL}/results-sheet/${id}`} alt=""
              onClick={()=>{setViewer(id);resetView();}}
              style={{width:34,height:34,objectFit:'cover',borderRadius:3,cursor:'pointer',border:'1px solid #cdd3e4'}}/>
            <button onClick={e=>handleRemove(id,e)}
              title="Remove"
              style={{position:'absolute',top:-5,right:-5,width:14,height:14,borderRadius:'50%',
                border:'none',background:'#dc2626',color:'white',fontSize:9,cursor:'pointer',lineHeight:'14px',padding:0}}>
              ✕
            </button>
          </div>
        ))}
      </div>
      {viewer && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.8)',zIndex:4000,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setViewer(null)}>
          <div style={{background:'white',borderRadius:8,padding:12,width:'98vw',height:'98vh',
            display:'flex',flexDirection:'column'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'flex-end',gap:6,marginBottom:8,flexShrink:0,alignItems:'center'}}>
              <button onClick={()=>setZoom(z=>Math.max(0.5,z-0.25))} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>−</button>
              <span style={{fontSize:11,color:'#555',minWidth:34,textAlign:'center'}}>{Math.round(zoom*100)}%</span>
              <button onClick={()=>setZoom(z=>Math.min(4,z+0.25))} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>+</button>
              <button onClick={()=>setRotation(r=>(r-90+360)%360)} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>↺</button>
              <button onClick={()=>setRotation(r=>(r+90)%360)} style={{fontSize:11,padding:'3px 8px',cursor:'pointer'}}>↻</button>
              <button onClick={()=>handlePrint(`${SERVER_URL}/results-sheet/${viewer}`, rotation)}
                style={{fontSize:11,padding:'3px 8px',cursor:'pointer',
                  background:'#0078d4',color:'white',border:'none',borderRadius:3}}>
                🖨 Print
              </button>
              <a href={`${SERVER_URL}/results-sheet/${viewer}`} download={`ResultsSheet_${sampleId}.jpg`}
                style={{fontSize:11,padding:'3px 8px',cursor:'pointer',textDecoration:'none',
                  background:'#16a34a',color:'white',borderRadius:3}}>
                ⬇ Download
              </a>
              <button onClick={()=>setViewer(null)} style={{fontSize:14,padding:'3px 8px',cursor:'pointer'}}>✕</button>
            </div>
            <div style={{flex:1,overflow:'auto',textAlign:'center',background:'#f4f5f7',borderRadius:6}}>
              <img src={`${SERVER_URL}/results-sheet/${viewer}`} alt=""
                style={{minWidth: zoom>1 ? `${zoom*100}%` : undefined,
                  width: zoom<=1 ? `${zoom*100}%` : undefined,
                  transform:`rotate(${rotation}deg)`,
                  transformOrigin:'center center', transition:'transform 0.3s',
                  margin:'20px auto', display:'block'}}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SampleSetup({ currentSample, onSelectSample, onNavigate, toast, session, isActive = false }) {
  const [activeTab, setActiveTab] = useState('home');
  const [showCOC, setShowCOC] = useState(false);
  const [paramSearch, setParamSearch] = useState('');
  const [selected,  setSelected]  = useState(new Set(currentSample?.params||[]));
  const selectionDirty = useRef(false);
  const selectedSampleId = useRef(String(currentSample?.id ?? ''));
  const [sample,    setSample]    = useState(currentSample);
  const [identityDraft, setIdentityDraft] = useState({
    id: currentSample?.id == null ? '' : String(currentSample.id),
    workOrder: currentSample?.workOrder || '',
  });
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [printingLabel, setPrintingLabel] = useState(false);
  const [addModal,  setAddModal]  = useState(null);
  const [areasTick, setAreasTick]  = useState(0);
  const [customPackages, setCustomPackages] = useState(() =>
    normalizeCustomPackages(syncedGet(CUSTOM_PACKAGES_KEY, []))
  );
  const [packageEditorOpen, setPackageEditorOpen] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [packageName, setPackageName] = useState('');
  const [savingPackage, setSavingPackage] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);
  const [workOrderSaveProgress, setWorkOrderSaveProgress] = useState(null);
  const { check: approvalCheck, modal: approvalModal } = useApprovalCheck(session); // refresh when admin changes areas
  const allSamples = db.getSamples();

  useEffect(() => {
    const owner = 'sample-setup-form';
    if (isActive) beginUiEdit(owner);
    else endUiEdit(owner);
    return () => endUiEdit(owner);
  }, [isActive]);

  // Dynamic tabs from ParamManager (includes custom areas)
  const dynamicTabs = useMemo(() => [
    ['home','🏠 Home'],
    ...getAllAreas().map(a => [a.key, a.label])
  ], [areasTick]);

  useEffect(() => {
    if (currentSample) {
      const changedSample = selectedSampleId.current !== String(currentSample.id);
      selectedSampleId.current = String(currentSample.id);
      setSample(currentSample);
      // A background refresh must not erase parameters the user has selected
      // but has not saved yet.
      if (changedSample || !selectionDirty.current) {
        selectionDirty.current = false;
        setSelected(new Set(currentSample.params||[]));
      }
      setIdentityDraft({
        id: currentSample.id == null ? '' : String(currentSample.id),
        workOrder: currentSample.workOrder || '',
      });
    }
  }, [currentSample]);

  const loadSample = (s) => {
    selectionDirty.current = false;
    selectedSampleId.current = String(s?.id ?? '');
    setParamSearch(s?.cocAnalysis || '');
    setSample(s);
    setSelected(new Set(s.params||[]));
    setIdentityDraft({
      id: s?.id == null ? '' : String(s.id),
      workOrder: s?.workOrder || '',
    });
    onSelectSample(s);
  };

  const toggle = (p) => {
    selectionDirty.current = true;
    setSelected(prev => { const n=new Set(prev); n.has(p)?n.delete(p):n.add(p); return n; });
  };

  const getBuiltInPackage = (key, entry) => {
    const saved = customPackages.find(pkg => pkg.id === `builtin:${key}`);
    if (saved) return { ...saved, builtIn:true, builtInKey:key };
    return {
      id:`builtin:${key}`,
      builtIn:true,
      builtInKey:key,
      label:entry?.label || key,
      params:Array.isArray(entry) ? entry : (entry?.params || []),
    };
  };

  const applyPackage = (pkg) => {
    const entry = AUTO_PACKAGES[pkg];
    const effective = getBuiltInPackage(pkg, entry);
    const defs = effective.params || [];
    selectionDirty.current = true;
    setSelected(prev => { const n=new Set(prev); defs.forEach(p=>n.add(p)); return n; });
    toast('success', `Applied package: ${effective.label} (${defs.length} parameters)`);
  };

  const applyCustomPackage = (pkg, replace = false) => {
    const defs = Array.isArray(pkg?.params) ? pkg.params : [];
    selectionDirty.current = true;
    setSelected(prev => {
      const next = replace ? new Set() : new Set(prev);
      defs.forEach(p => next.add(p));
      return next;
    });
    toast('success', `${replace ? 'Loaded' : 'Applied'} package: ${pkg.label} (${defs.length} parameters)`);
  };

  const beginNewPackage = () => {
    setEditingPackageId(null);
    setPackageName('');
    setPackageEditorOpen(true);
  };

  const beginEditPackage = (pkg) => {
    setEditingPackageId(pkg.id);
    setPackageName(pkg.label);
    setPackageEditorOpen(true);
  };

  const closePackageEditor = () => {
    setEditingPackageId(null);
    setPackageName('');
    setPackageEditorOpen(false);
  };

  const persistCustomPackages = async (nextPackages, successMessage) => {
    const previous = customPackages;
    setCustomPackages(nextPackages);
    setSavingPackage(true);
    try {
      await syncedSet(CUSTOM_PACKAGES_KEY, nextPackages);
      toast('success', successMessage);
      closePackageEditor();
    } catch (error) {
      setCustomPackages(previous);
      toast('error', error.message || 'Could not save the quick package');
    } finally {
      setSavingPackage(false);
    }
  };

  const saveCustomPackage = () => {
    const label = packageName.trim();
    if (!label) {
      toast('error', 'Enter a package name');
      return;
    }
    if (selected.size === 0) {
      toast('error', 'Select at least one parameter before saving the package');
      return;
    }
    const editingBuiltIn = String(editingPackageId || '').startsWith('builtin:');
    const duplicate = customPackages.find(pkg =>
      pkg.label.toLowerCase() === label.toLowerCase() &&
      pkg.id !== editingPackageId &&
      !(editingBuiltIn && !String(pkg.id).startsWith('builtin:'))
    );
    if (duplicate) {
      toast('error', `A quick package named "${label}" already exists`);
      return;
    }

    const params = [...selected];
    if (editingPackageId) {
      const exists = customPackages.some(pkg => pkg.id === editingPackageId);
      const updated = {
        ...(customPackages.find(pkg => pkg.id === editingPackageId) || {}),
        id:editingPackageId,
        label,
        params,
        ...(editingBuiltIn ? {
          builtIn:true,
          builtInKey:editingPackageId.slice('builtin:'.length),
        } : {}),
      };
      const next = exists
        ? customPackages.map(pkg => pkg.id === editingPackageId ? updated : pkg)
        : [...customPackages, updated];
      persistCustomPackages(next, `Updated quick package: ${label}`);
      return;
    }

    const next = [
      ...customPackages,
      { id:`custom-${Date.now()}`, label, params },
    ];
    persistCustomPackages(next, `Created quick package: ${label}`);
  };

  const deleteCustomPackage = (pkg) => {
    const isBuiltIn = String(pkg.id).startsWith('builtin:');
    const action = isBuiltIn ? 'reset' : 'delete';
    if (!window.confirm(
      isBuiltIn
        ? `Reset "${pkg.label}" to its original package name and parameters?`
        : `Delete quick package "${pkg.label}"? This does not remove parameters from samples.`
    )) return;
    const next = customPackages.filter(item => item.id !== pkg.id);
    persistCustomPackages(next,
      isBuiltIn ? `Reset quick package to default: ${pkg.label}` : `Deleted quick package: ${pkg.label}`
    );
  };

  const refreshActiveSample = () => {
    const updated = db.getSamples().find(s => String(s.id) === String(sample?.id));
    if (!updated) return;
    setSample(updated);
    onSelectSample(updated);
  };

  const performSaveSetup = async () => {
    if (!sample || savingSetup || workOrderSaveProgress) return;
    setSavingSetup(true);
    try {
      const intended = [...selected];
      const saved = await db.updateSample(sample.id, { params:intended });
      if (JSON.stringify(saved?.record?.params) !== JSON.stringify(intended)) {
        throw new Error(`Server did not confirm all ${intended.length} parameters for Sample ${sample.id}`);
      }
      selectionDirty.current = false;
      refreshActiveSample();
      toast('success', `Setup saved — ${intended.length} parameters for Sample ${sample.id}`);
    } catch (error) {
      toast('error', error.message || `Could not save setup for Sample ${sample.id}`);
    } finally {
      setSavingSetup(false);
    }
  };

  const saveSetup = () => {
    if (!sample || savingSetup || workOrderSaveProgress) return;
    performSaveSetup();
  };

  const saveSetupForWorkOrder = async () => {
    if (!sample || savingSetup || workOrderSaveProgress) return;
    const targets = db.getSamples()
      .filter(s => String(s.workOrder || '') === String(sample.workOrder || ''))
      .sort((a,b) => Number(a.id) - Number(b.id));
    if (!sample.workOrder || targets.length === 0) {
      toast('error', 'No samples were found for this Work Order');
      return;
    }
    if (selected.size === 0) {
      toast('error', 'Select at least one parameter before applying the setup to the Work Order');
      return;
    }
    const confirmed = window.confirm(
      `Apply these ${selected.size} parameters to all ${targets.length} samples in Work Order ${sample.workOrder}?\n\n` +
      'This replaces the current parameter selection on every sample in this Work Order.'
    );
    if (!confirmed) return;

    const params = [...selected];
    const failures = [];
    setWorkOrderSaveProgress({ done:0, total:targets.length });
    for (let index = 0; index < targets.length; index += 1) {
      const target = targets[index];
      try {
        const saved = await db.updateSample(target.id, { params });
        if (JSON.stringify(saved?.record?.params) !== JSON.stringify(params)) {
          throw new Error(`Server did not confirm all parameters for Sample ${target.id}`);
        }
      } catch (error) {
        failures.push({
          id: target.id,
          message: error.message || 'Server update failed',
        });
      }
      setWorkOrderSaveProgress({ done:index + 1, total:targets.length });
    }

    refreshActiveSample();
    setWorkOrderSaveProgress(null);
    if (failures.length > 0) {
      const failedIds = failures.map(item => item.id).join(', ');
      toast(
        'error',
        `Saved ${targets.length - failures.length} of ${targets.length} samples. ` +
        `Not saved: ${failedIds}. Please retry those samples.`
      );
      return;
    }
    selectionDirty.current = false;
    toast(
      'success',
      `Saved ${selected.size} parameters to all ${targets.length} samples in Work Order ${sample.workOrder}`
    );
  };

  const handlePrintLabel = async () => {
    if (!sample || printingLabel) return;
    setPrintingLabel(true);
    try {
      const fresh = db.getSamples().find(s => String(s.id) === String(sample.id)) || sample;
      printSampleLabels([fresh]);
    } catch (error) {
      toast('error', error.message || 'Could not print the sample label.');
    } finally {
      setPrintingLabel(false);
    }
  };

  const handlePrintWorkOrderLabels = () => {
    if (!sample?.workOrder || printingLabel) return;
    const targets = db.getSamples()
      .filter(s => String(s.workOrder || '') === String(sample.workOrder))
      .sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric:true }));
    try { printSampleLabels(targets); }
    catch (error) { toast('error', error.message || 'Could not open work order labels.'); }
  };

  const saveIdentity = () => {
    if (!sample || savingIdentity) return;
    const newId = identityDraft.id.trim();
    const newWorkOrder = identityDraft.workOrder.trim();
    const oldId = String(sample.id);
    const oldWorkOrder = String(sample.workOrder || '');
    if (!newId) {
      toast('error', 'Sample ID is required');
      return;
    }
    if (!newWorkOrder) {
      toast('error', 'Work Order is required');
      return;
    }
    const duplicate = db.getSamples().find(s =>
      String(s.id) === newId && String(s.id) !== oldId
    );
    if (duplicate) {
      toast('error', `Sample ID ${newId} already exists`);
      return;
    }
    if (newId === oldId && newWorkOrder === oldWorkOrder) {
      toast('info', 'Sample ID and Work Order are unchanged');
      return;
    }
    if (newId !== oldId && !window.confirm(
      `Change Sample ID ${oldId} to ${newId}?\n\nResults and linked records will move to the new Sample ID.`
    )) return;

    const performUpdate = async () => {
      setSavingIdentity(true);
      try {
        const result = await db.updateSampleIdentity(oldId, newId, newWorkOrder);
        const updated = result.record;
        setSample(updated);
        setIdentityDraft({
          id: String(updated.id),
          workOrder: updated.workOrder || '',
        });
        onSelectSample(updated);
        toast(
          'success',
          `Sample ${updated.id} is now grouped under Work Order ${updated.workOrder}`
        );
      } catch (error) {
        toast('error', error.message || 'Could not update Sample ID / Work Order');
      } finally {
        setSavingIdentity(false);
      }
    };

    approvalCheck({
      sampleId: sample.id,
      sample,
      changeType: 'edit_identity',
      description: `Change Sample ${oldId} / Work Order ${oldWorkOrder} to Sample ${newId} / Work Order ${newWorkOrder}`,
      proposedChanges: { sampleId: newId, workOrder: newWorkOrder },
      currentValues: { sampleId: oldId, workOrder: oldWorkOrder },
      onAllowed: performUpdate,
    });
  };

  const openAddToWO = (copyAll) => {
    if (!sample) return;
    const newId = db.getCounters().nextSampleNum;
    setAddModal({
      workOrder: sample.workOrder,
      source: sample.source,
      client: sample.client||'',
      desc: sample.desc||'',
      testCategory: sample.testCategory||'',
      testType: sample.testType||'',
      rush: sample.rush||'5',
      tech: sample.tech||'',
      dueDate: sample.dueDate||weekOut(),
      received: today(),
      sampleForm: sample.sampleForm||'',
      amount: sample.amount||'',
      params: copyAll ? [...selected] : [],
      newId,
      _copyAll: copyAll,
    });
  };

  const confirmAdd = () => {
    if (!addModal) return;
    db.addSample({
      ...addModal,
      id: addModal.newId,
      params: addModal.params,
      results: {},
      completed: false,
      billed: false,
    });
    toast('success', `Sample ${addModal.newId} added to Work Order ${addModal.workOrder}${addModal._copyAll ? ' — all parameters copied' : ' — blank setup'}`);
    setAddModal(null);
  };

  const printCurrent = () => {
    if (!sample) return;
    const fresh = db.getSamples().find(s=>String(s.id)===String(sample.id))||sample;
    openPrint(buildWorksheetHTML([fresh], 'single'));
  };

  const printWOAll = () => {
    if (!sample) return;
    const woSamples = db.getSamples()
      .filter(s=>s.workOrder===sample.workOrder)
      .sort((a,b)=>a.id-b.id);
    openPrint(buildWorksheetHTML(woSamples, 'workorder'));
  };

  const printWOLaboratorySheets = () => {
    if (!sample) return;
    const woSamples = db.getSamples()
      .filter(s=>s.workOrder===sample.workOrder)
      .sort((a,b)=>a.id-b.id);
    openPrint(buildWorksheetHTML(woSamples, 'laboratory'));
  };

  const estPrice = selected.size * 25;
  const existingWorkOrders = [...new Set(
    allSamples.map(s => String(s.workOrder || '').trim()).filter(Boolean)
  )].sort((a,b) => a.localeCompare(b, undefined, { numeric:true }));
  const workOrderSamples = sample
    ? db.getSamples().filter(s=>s.workOrder===sample.workOrder).sort((a,b)=>a.id-b.id)
    : [];
  const companyContacts = sample
    ? getCompanyContacts(sample.client || sample.source || '', sample)
    : [];

  const renderHomeTab = () => (
    <div>
      {!sample ? (
        <div className="notice notice-warn">
          ← Select a sample from the sidebar to set up parameters.
        </div>
      ) : (
        <>
          <div className="notice notice-info" style={{marginBottom:10}}>
            Setting up Sample <strong>{sample.id}</strong> — {sample.source} — {sample.testCategory}
            &nbsp;|&nbsp; Work Order: <strong>{sample.workOrder}</strong>
            &nbsp;|&nbsp; {workOrderSamples.length} sample{workOrderSamples.length!==1?'s':''} in this WO
          </div>

          <div style={{border:'1px solid #cdd7e8',borderRadius:6,padding:'8px 12px',
            marginBottom:10,background:'#f8fbff',fontSize:11}}>
            <strong style={{color:'#1e3060'}}>Company Contacts:</strong>{' '}
            {companyContacts.length
              ? companyContacts.map(contact => `${contact.name || 'Contact'}${contact.email ? ` <${contact.email}>` : ''}`).join('; ')
              : 'No saved contact yet — add one in Sample Receiving.'}
          </div>

          <div style={{border:'1px solid #cdd7e8',borderRadius:6,padding:'10px 12px',
            marginBottom:10,background:'#f8fbff'}}>
            <div style={{fontSize:11,fontWeight:700,color:'#1e3060',marginBottom:7}}>
              Sample Identity &amp; Work Order Grouping
            </div>
            <div style={{display:'grid',gridTemplateColumns:'minmax(130px,1fr) minmax(170px,1.4fr) auto',
              gap:8,alignItems:'end'}}>
              <label style={{fontSize:10,fontWeight:600,color:'#52617a'}}>
                Sample ID
                <input value={identityDraft.id}
                  onChange={e=>setIdentityDraft(d=>({...d,id:e.target.value}))}
                  style={{display:'block',width:'100%',boxSizing:'border-box',marginTop:3,
                    border:'1px solid #b8c5da',borderRadius:4,padding:'6px 8px',fontSize:12}}/>
              </label>
              <label style={{fontSize:10,fontWeight:600,color:'#52617a'}}>
                Work Order
                <input value={identityDraft.workOrder} list="sample-setup-work-orders"
                  onChange={e=>setIdentityDraft(d=>({...d,workOrder:e.target.value}))}
                  onKeyDown={e=>{ if(e.key==='Enter') saveIdentity(); }}
                  placeholder="Type or select an existing Work Order"
                  style={{display:'block',width:'100%',boxSizing:'border-box',marginTop:3,
                    border:'1px solid #b8c5da',borderRadius:4,padding:'6px 8px',fontSize:12}}/>
                <datalist id="sample-setup-work-orders">
                  {existingWorkOrders.map(wo=><option key={wo} value={wo}/>)}
                </datalist>
              </label>
              <button className="btn btn-primary" onClick={saveIdentity}
                disabled={savingIdentity}
                style={{height:30,whiteSpace:'nowrap'}}>
                {savingIdentity ? 'Saving…' : 'Save ID / Group'}
              </button>
            </div>
            <div style={{fontSize:10,color:'#6b7280',marginTop:6}}>
              Select an existing Work Order to add this sample to that report and Chain of Custody group.
            </div>
          </div>

          <ResultsSheetPanel sampleId={sample.id} toast={toast}/>

          {/* Work Order siblings */}
          {workOrderSamples.length > 1 && (
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:700,color:'#1e3060',marginBottom:5}}>
                All Samples in Work Order {sample.workOrder}
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {workOrderSamples.map(s=>(
                  <button key={s.id}
                    onClick={()=>loadSample(s)}
                    style={{padding:'4px 12px',borderRadius:14,border:'1px solid',
                      fontSize:11,fontWeight:700,cursor:'pointer',
                      borderColor: String(s.id)===String(sample.id)?'#1e3060':'#cdd3e4',
                      background: String(s.id)===String(sample.id)?'#1e3060':'white',
                      color: String(s.id)===String(sample.id)?'white':'#1e3060'}}>
                    #{s.id} {(s.params||[]).length>0?`(${s.params.length}p)`:'(—)'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick packages */}
          <div style={{marginBottom:10}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
              <div style={{fontSize:11,fontWeight:700,color:'#1e3060'}}>
                Quick Packages
              </div>
              <button type="button" className="btn btn-sm" onClick={beginNewPackage}
                style={{fontSize:10,padding:'3px 8px'}}>
                + New Package
              </button>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {Object.entries(AUTO_PACKAGES).map(([key, entry])=>{
                const pkg = getBuiltInPackage(key, entry);
                return (
                  <span key={pkg.id} style={{display:'inline-flex',alignItems:'stretch'}}>
                    <button type="button" className="btn btn-sm"
                      onClick={()=>applyPackage(key)}
                      title={`Add ${pkg.params.length} saved parameters`}
                      style={{fontSize:11,borderTopRightRadius:0,borderBottomRightRadius:0}}>
                      + {pkg.label}
                    </button>
                    <button type="button" className="btn btn-sm"
                      onClick={()=>beginEditPackage(pkg)}
                      title={`Edit ${pkg.label}`}
                      aria-label={`Edit ${pkg.label}`}
                      style={{fontSize:10,padding:'3px 7px',borderLeft:0,
                        borderTopLeftRadius:0,borderBottomLeftRadius:0}}>
                      ✎
                    </button>
                  </span>
                );
              })}
              {customPackages.filter(pkg=>!String(pkg.id).startsWith('builtin:')).map(pkg=>(
                <span key={pkg.id} style={{display:'inline-flex',alignItems:'stretch'}}>
                  <button type="button" className="btn btn-sm"
                    onClick={()=>applyCustomPackage(pkg)}
                    title={`Add ${pkg.params.length} saved parameters`}
                    style={{fontSize:11,borderTopRightRadius:0,borderBottomRightRadius:0}}>
                    + {pkg.label}
                  </button>
                  <button type="button" className="btn btn-sm"
                    onClick={()=>beginEditPackage(pkg)}
                    title={`Edit ${pkg.label}`}
                    aria-label={`Edit ${pkg.label}`}
                    style={{fontSize:10,padding:'3px 7px',borderLeft:0,
                      borderTopLeftRadius:0,borderBottomLeftRadius:0}}>
                    ✎
                  </button>
                </span>
              ))}
            </div>
            <div style={{fontSize:9,color:'#6b7280',marginTop:5}}>
              Every package has an edit button (✎). Package changes are saved on the server for all computers.
            </div>
            {packageEditorOpen && (
              <div style={{marginTop:8,padding:10,border:'1px solid #b8c5da',
                borderRadius:5,background:'#f7f9fd',maxWidth:760}}>
                <div style={{fontSize:11,fontWeight:700,color:'#1e3060',marginBottom:6}}>
                  {editingPackageId ? 'Edit Quick Package' : 'Create Quick Package'}
                </div>
                <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                  <input value={packageName} onChange={e=>setPackageName(e.target.value)}
                    onKeyDown={e=>{ if(e.key==='Enter') saveCustomPackage(); }}
                    placeholder="Package name"
                    autoFocus
                    style={{width:240,border:'1px solid #b8c5da',borderRadius:4,
                      padding:'6px 8px',fontSize:11}}/>
                  <button type="button" className="btn btn-primary btn-sm"
                    disabled={savingPackage} onClick={saveCustomPackage}
                    style={{fontSize:10}}>
                    {savingPackage ? 'Saving…' :
                      editingPackageId ? `Save Name + Current ${selected.size} Parameters` :
                        `Save Current ${selected.size} Parameters`}
                  </button>
                  {editingPackageId && (() => {
                    const saved = customPackages.find(item=>item.id===editingPackageId);
                    const builtInKey = String(editingPackageId).startsWith('builtin:')
                      ? String(editingPackageId).slice('builtin:'.length)
                      : null;
                    const pkg = saved || (builtInKey && AUTO_PACKAGES[builtInKey]
                      ? getBuiltInPackage(builtInKey, AUTO_PACKAGES[builtInKey])
                      : null);
                    return pkg ? (
                      <>
                        <button type="button" className="btn btn-sm"
                          onClick={()=>applyCustomPackage(pkg, true)}
                          style={{fontSize:10}}>
                          Load Saved Parameters ({pkg.params.length})
                        </button>
                        <button type="button" className="btn btn-sm"
                          disabled={savingPackage} onClick={()=>deleteCustomPackage(pkg)}
                          style={{fontSize:10,color:'#991b1b',borderColor:'#f0a0a0'}}>
                          {String(pkg.id).startsWith('builtin:') ? 'Reset to Default' : 'Delete Package'}
                        </button>
                      </>
                    ) : null;
                  })()}
                  <button type="button" className="btn btn-sm" onClick={closePackageEditor}
                    disabled={savingPackage} style={{fontSize:10}}>
                    Cancel
                  </button>
                </div>
                <div style={{fontSize:9,color:'#6b7280',marginTop:6}}>
                  The package will use the parameters currently selected for this sample.
                  {editingPackageId ? ' Use “Load Saved Parameters” before changing an existing package if needed.' : ''}
                </div>
              </div>
            )}
          </div>

          <div style={{fontSize:11,color:'#888',marginTop:4}}>
            {selected.size} parameters selected · Est. price: ${estPrice}
          </div>
        </>
      )}
    </div>
  );

  const renderParamTab = (tabKey) => {
    const raw = getMergedParams(tabKey);
    const defs = raw.map(p => typeof p === 'object' ? p.name : p).filter(Boolean);
    if (defs.length===0) return <div className="notice notice-info">No parameters — add some in Admin → Test Parameters.</div>;
    const matchSet = paramSearch
      ? new Set(defs.filter(p => p.toLowerCase().includes(paramSearch.toLowerCase())))
      : new Set();
    return (
      <div>
        <div style={{marginBottom:10,display:'flex',gap:8,alignItems:'center'}}>
          <input value={paramSearch} onChange={e=>setParamSearch(e.target.value)}
            placeholder="🔍 Search parameters... (pre-filled from COC)"
            style={{flex:1,border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 10px',
              fontSize:12,outline:'none',
              background:paramSearch?'#fffbe6':'white',
              borderColor:paramSearch?'#f59e0b':'#cdd3e4'}}/>
          {paramSearch && <>
            <span style={{fontSize:11,color:'#92400e',fontWeight:600,whiteSpace:'nowrap'}}>
              {matchSet.size} match{matchSet.size!==1?'es':''}
            </span>
            <button onClick={()=>setParamSearch('')}
              style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 8px',
                cursor:'pointer',fontSize:11,background:'white',color:'#666'}}>
              ✕ Clear
            </button>
          </>}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px 16px'}}>
          {defs.map(p=>{
            const isMatch = matchSet.size>0 && matchSet.has(p);
            if (paramSearch && !isMatch) return null;
            return (
              <label key={p} style={{display:'flex',alignItems:'center',gap:7,
                padding:'5px 8px',borderRadius:4,marginBottom:4,cursor:'pointer',
                breakInside:'avoid',
                background:selected.has(p)?'#e8f0ff':isMatch?'#fffbe6':'transparent',
                border:`1px solid ${selected.has(p)?'#1e3060':isMatch?'#f59e0b':'transparent'}`}}>
                <input type="checkbox" checked={selected.has(p)} onChange={()=>toggle(p)}
                  style={{accentColor:'#1e3060',width:14,height:14}}/>
                <span style={{fontSize:12,color:selected.has(p)?'#1e3060':isMatch?'#92400e':'#333',
                  fontWeight:selected.has(p)||isMatch?600:400}}>{p}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
    {showCOC && sample?.cocId && (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.75)',zIndex:4000,
        display:'flex',alignItems:'center',justifyContent:'center'}}
        onClick={()=>setShowCOC(false)}>
        <div style={{background:'white',borderRadius:8,padding:16,maxWidth:'92vw',maxHeight:'92vh',
          overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}
          onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <strong style={{fontSize:14}}>📄 Chain of Custody — Sample {sample.id} (WO: {sample.workOrder})</strong>
            <button onClick={()=>setShowCOC(false)}
              style={{border:'none',background:'none',fontSize:20,cursor:'pointer',color:'#666'}}>✕</button>
          </div>
          <img src={`${SERVER_URL}/coc/${sample.cocId}`} alt="COC"
            style={{maxWidth:'100%',maxHeight:'80vh',objectFit:'contain',display:'block'}}/>
        </div>
      </div>
    )}
    <div style={{display:'flex',flex:1,overflow:'hidden',minHeight:0}}>

      {/* ── Sidebar ── */}
      <Sidebar
        allSamples={allSamples}
        activeSampleId={sample?.id}
        onSelect={loadSample}
        onSelectSample={onSelectSample}
      />

      {/* ── Main area ── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minWidth:0}}>

        {/* Toolbar */}
        <div className="toolbar">
          <button className="btn btn-primary" onClick={saveSetup}
            disabled={!sample || savingSetup || !!workOrderSaveProgress}>
            {savingSetup ? 'Saving…' : '💾 Save This Sample'}
          </button>
          <button className="btn" onClick={saveSetupForWorkOrder}
            disabled={!sample || savingSetup || !!workOrderSaveProgress || selected.size===0}
            title={`Replace the parameter setup on all ${workOrderSamples.length} samples in this Work Order`}
            style={{background:'#1a6b42',borderColor:'#1a6b42',color:'white',fontWeight:700}}>
            {workOrderSaveProgress
              ? `Saving ${workOrderSaveProgress.done}/${workOrderSaveProgress.total}…`
              : `💾 Apply & Save All ${workOrderSamples.length} in WO`}
          </button>
          {sample?.cocId && (
            <button className="btn" onClick={()=>setShowCOC(true)}
              style={{background:'#e8f0ff',borderColor:'#1e3060',color:'#1e3060',fontWeight:600}}>
              📄 View COC
            </button>
          )}
          <div className="tb-sep"/>
          {/* Print options */}
          <button className="btn" onClick={printWOAll} disabled={!sample}
            title={`Print Work Order ${sample?.workOrder} without laboratory worksheets`}>
            🖨 Print Work Order ({workOrderSamples.length})
          </button>
          <button className="btn" onClick={printCurrent} disabled={!sample}
            title={`Print the laboratory worksheet for Sample ${sample?.id}`}>
            🧪 Print Lab Sheet — Sample
          </button>
          <button className="btn" onClick={printWOLaboratorySheets} disabled={!sample}
            title={`Print ${workOrderSamples.length} laboratory worksheets for Work Order ${sample?.workOrder}`}>
            🧪 Print Lab Sheets — WO ({workOrderSamples.length})
          </button>
          <div className="tb-sep"/>
          <button className="btn" onClick={()=>openAddToWO(false)} disabled={!sample}>
            ＋ Add to WO (Blank)
          </button>
          <button className="btn" onClick={()=>openAddToWO(true)} disabled={!sample}>
            ⧉ Copy to Next Sample
          </button>
          <div className="tb-sep"/>
          <span className="tb-label">Sample:</span>
          <span className="tb-value mono">{sample?.id||'—'}</span>
          <span className="tb-label" style={{marginLeft:8}}>WO:</span>
          <span className="tb-value mono">{sample?.workOrder||'—'}</span>
          <button className="btn btn-sm" onClick={handlePrintLabel}
            disabled={!sample || printingLabel}
            style={{marginLeft:6,background:'#edf8ee',borderColor:'#9ac79d',color:'#245b29'}}
            title="Print the selected sample ID, company and description">
            {printingLabel ? 'Opening…' : '🏷 Print Label — Sample'}
          </button>
          <button className="btn btn-sm" onClick={handlePrintWorkOrderLabels}
            disabled={!sample?.workOrder || printingLabel}
            style={{marginLeft:6,background:'#edf8ee',borderColor:'#9ac79d',color:'#245b29'}}
            title={`Print one label for each saved sample in Work Order ${sample?.workOrder || ''}`}>
            🏷 Print Labels — Work Order ({workOrderSamples.length})
          </button>
          {selected.size>0 && <>
            <div className="tb-sep"/>
            <span className="tb-label">{selected.size} params</span>
          </>}
        </div>

        {/* Tab bar — dynamic from ParamManager */}
        <div style={{background:'#f0f2f8',display:'flex',gap:0,
          padding:'0 8px',flexShrink:0,borderBottom:'2px solid #e0e4ec',
          overflowX:'auto'}}>
          {dynamicTabs.map(([key,label])=>(
            <button key={key}
              style={{padding:'8px 14px',border:'none',background:'none',
                cursor:'pointer',fontSize:12,fontWeight:600,whiteSpace:'nowrap',
                color:activeTab===key?'#1e3060':'#7788aa',
                borderBottom:activeTab===key?'2px solid #1e3060':'2px solid transparent',
                display:'flex',alignItems:'center',gap:4}}
              onClick={()=>setActiveTab(key)}>
              {label}
              {key!=='home' && (() => {
                const params = getMergedParams(key).map(p=>p.name||p);
                const cnt = params.filter(p=>selected.has(p)).length;
                return cnt>0 ? (
                  <span style={{marginLeft:5,background:'#1e3060',color:'white',
                    borderRadius:8,padding:'1px 5px',fontSize:9,fontWeight:700}}>
                    {cnt}
                  </span>
                ) : null;
              })()}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{flex:1,overflowY:'auto',padding:'14px 16px',background:'white'}}>
          {activeTab==='home' ? renderHomeTab() : renderParamTab(activeTab)}
        </div>
      </div>

      {/* ── Add to WO Modal ── */}
      {addModal && (
        <div className="modal-overlay" onClick={()=>setAddModal(null)}>
          <div className="modal" style={{width:620}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>{addModal._copyAll?'⧉ Copy to Next Sample':'＋ Add Sample to Work Order'} — {addModal.workOrder}</h2>
              <button className="modal-close" onClick={()=>setAddModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-section-title">New Sample — Work Order {addModal.workOrder}</div>
              <div className="form-grid">
                <div className="form-group"><label>New Sample #</label>
                  <input readOnly value={addModal.newId}
                    style={{background:'#f0f4ff',fontWeight:700,fontFamily:'monospace'}}/></div>
                <div className="form-group"><label>Work Order</label>
                  <input readOnly value={addModal.workOrder} style={{background:'#f0f4ff'}}/></div>
                <div className="form-group"><label>Source / Client</label>
                  <input value={addModal.source}
                    onChange={e=>setAddModal(p=>({...p,source:e.target.value}))}/></div>
                <div className="form-group"><label>Description</label>
                  <input value={addModal.desc}
                    onChange={e=>setAddModal(p=>({...p,desc:e.target.value}))}/></div>
                <div className="form-group"><label>Test Category</label>
                  <input value={addModal.testCategory}
                    onChange={e=>setAddModal(p=>({...p,testCategory:e.target.value}))}/></div>
                <div className="form-group"><label>Assigned To</label>
                  <input value={addModal.tech}
                    onChange={e=>setAddModal(p=>({...p,tech:e.target.value}))}/></div>
                <div className="form-group"><label>Received Date</label>
                  <input type="date" value={addModal.received}
                    onChange={e=>setAddModal(p=>({...p,received:e.target.value}))}/></div>
                <div className="form-group"><label>Due Date</label>
                  <input type="date" value={addModal.dueDate}
                    onChange={e=>setAddModal(p=>({...p,dueDate:e.target.value}))}/></div>
                <div className="form-group"><label>Rush Level</label>
                  <select value={addModal.rush}
                    onChange={e=>setAddModal(p=>({...p,rush:e.target.value}))}>
                    <option value="1">1 — Same Day</option>
                    <option value="2">2 — Next Day</option>
                    <option value="3">3 — 2-3 Days</option>
                    <option value="4">4 — 4-5 Days</option>
                    <option value="5">5 — Standard</option>
                  </select></div>
              </div>
              {addModal._copyAll && (
                <div className="notice notice-info" style={{marginTop:8}}>
                  ⧉ All {addModal.params.length} selected parameters will be copied to the new sample.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={confirmAdd}>
                {addModal._copyAll
                  ? `⧉ Create Sample #${addModal.newId} with ${addModal.params.length} params`
                  : `＋ Create Sample #${addModal.newId} in WO ${addModal.workOrder}`}
              </button>
              <button className="btn" onClick={()=>setAddModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
